import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, PlusCircle, LogOut, Sparkles, ClipboardList, Settings,
  Copy, Download, FileJson, FileSpreadsheet, Loader2, CheckCircle, Database, Filter,
  Search, User, X
} from 'lucide-react';
import Logo from '../../components/common/Logo';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Opções de filtro por status
const FILTER_OPTIONS = [
  { value: 'all', label: 'Todas as mães', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  { value: 'approved', label: 'Planos aprovados', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'pending_analysis', label: 'Aguardando análise', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'pending_routine', label: 'Registrando rotina', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { value: 'pending_form', label: 'Aguardando formulário', color: 'bg-gray-100 text-gray-600 border-gray-300' },
];

const AdminExportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [exporting, setExporting] = useState(null);
  const [lastExport, setLastExport] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  
  // Estados para seleção individual
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // Obter token de autenticação
  const getAuthToken = () => {
    return localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
  };

  // Carregar lista de relatórios
  useEffect(() => {
    const loadReports = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_URL}/api/reports`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setReports(data);
        }
      } catch (error) {
        console.error('Erro ao carregar relatórios:', error);
      } finally {
        setLoadingReports(false);
      }
    };
    loadReports();
  }, []);

  // Filtrar relatórios pela busca
  const filteredReports = reports.filter(r => {
    const search = searchTerm.toLowerCase();
    return (
      (r.baby_name && r.baby_name.toLowerCase().includes(search)) ||
      (r.mother_name && r.mother_name.toLowerCase().includes(search)) ||
      (r.email && r.email.toLowerCase().includes(search))
    );
  });

  // Obter label do filtro selecionado
  const getFilterLabel = () => {
    if (selectedReport) {
      return `${selectedReport.baby_name} (${selectedReport.mother_name || 'mãe'})`;
    }
    const filter = FILTER_OPTIONS.find(f => f.value === selectedFilter);
    return filter ? filter.label : 'Todas';
  };

  // Construir URL com filtros
  const buildExportUrl = (endpoint) => {
    let url = `${API_URL}/api/export/${endpoint}`;
    const params = new URLSearchParams();
    
    if (selectedReport) {
      params.append('report_id', selectedReport.id);
    } else if (selectedFilter !== 'all') {
      params.append('status', selectedFilter);
    }
    
    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
  };

  // Selecionar uma mãe específica
  const handleSelectReport = (report) => {
    setSelectedReport(report);
    setSearchTerm('');
    setShowDropdown(false);
    setSelectedFilter('individual'); // Marca como seleção individual
  };

  // Limpar seleção individual
  const clearSelection = () => {
    setSelectedReport(null);
    setSelectedFilter('all');
  };

  // Exportar JSON
  const handleExportJSON = async () => {
    setExporting('json');
    try {
      const token = getAuthToken();
      const url = buildExportUrl('json');
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erro ao exportar dados');
      
      const data = await response.json();
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url2 = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url2;
      const filename = selectedReport 
        ? `export_${selectedReport.baby_name?.replace(/\s+/g, '_') || 'mae'}_${new Date().toISOString().slice(0, 10)}.json`
        : `export_${selectedFilter}_${new Date().toISOString().slice(0, 10)}.json`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url2);
      document.body.removeChild(a);
      
      const recordCount = Array.isArray(data) ? data.length : 0;
      setLastExport({ type: 'JSON', records: recordCount, filter: getFilterLabel(), time: new Date() });
      toast.success(`Exportado com sucesso! ${recordCount} registro(s).`);
    } catch (error) {
      console.error('Erro ao exportar JSON:', error);
      toast.error('Erro ao exportar dados JSON');
    } finally {
      setExporting(null);
    }
  };

  // Exportar CSV de clientes
  const handleExportCSV = async () => {
    setExporting('csv');
    try {
      const token = getAuthToken();
      const url = buildExportUrl('csv');
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erro ao exportar dados');
      
      const blob = await response.blob();
      const url2 = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url2;
      const filename = selectedReport 
        ? `export_${selectedReport.baby_name?.replace(/\s+/g, '_') || 'mae'}_${new Date().toISOString().slice(0, 10)}.csv`
        : `export_clientes_${selectedFilter}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url2);
      document.body.removeChild(a);
      
      setLastExport({ type: 'CSV (Clientes)', filter: getFilterLabel(), time: new Date() });
      toast.success('Arquivo CSV exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar dados CSV');
    } finally {
      setExporting(null);
    }
  };

  // Exportar CSV de rotinas
  const handleExportRoutines = async () => {
    setExporting('routines');
    try {
      const token = getAuthToken();
      const url = buildExportUrl('csv/routines');
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erro ao exportar rotinas');
      
      const blob = await response.blob();
      const url2 = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url2;
      const filename = selectedReport 
        ? `export_rotinas_${selectedReport.baby_name?.replace(/\s+/g, '_') || 'mae'}_${new Date().toISOString().slice(0, 10)}.csv`
        : `export_rotinas_${selectedFilter}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url2);
      document.body.removeChild(a);
      
      setLastExport({ type: 'CSV (Rotinas)', filter: getFilterLabel(), time: new Date() });
      toast.success('Arquivo de rotinas exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar rotinas:', error);
      toast.error('Erro ao exportar rotinas');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#FAE0D5] p-6 hidden lg:block">
        <div className="mb-8">
          <Link to="/admin/dashboard">
            <Logo variant="v3SonoRotina" size="md" className="cursor-pointer hover:opacity-80 transition-opacity" />
          </Link>
        </div>

        <nav className="space-y-2">
          {[
            { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
            { icon: PlusCircle, label: 'Novo Relatório', path: '/admin/new' },
            { icon: ClipboardList, label: 'Inserir Manualmente', path: '/admin/manual' },
            { icon: FileText, label: 'Relatórios', path: '/admin/reports' },
            { icon: Copy, label: 'Templates', path: '/admin/templates' },
            { icon: Sparkles, label: 'Treinar IA', path: '/admin/training' },
            { icon: Settings, label: 'Editor de Formulário', path: '/admin/form-builder' },
            { icon: Database, label: 'Exportar Dados', path: '/admin/export' }
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#8F7AC5] text-white'
                  : 'text-[#6B5B8C] hover:bg-[#FBF4FF]'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#9890A8] hover:text-[#30244F] hover:bg-[#FBF4FF]"
            onClick={() => navigate('/admin')}
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/admin/dashboard">
              <Logo variant="v3SonoRotina" size="sm" />
            </Link>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-[#9890A8]"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          {/* Mobile Navigation Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[
              { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
              { icon: FileText, label: 'Relatórios', path: '/admin/reports' },
              { icon: Database, label: 'Exportar', path: '/admin/export' }
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  location.pathname === item.path
                    ? 'bg-[#8F7AC5] text-white'
                    : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#30244F]">Exportar Dados</h1>
            <p className="text-[#6B5B8C] mt-1">Exporte os dados das clientes em diferentes formatos</p>
          </div>
        </div>

        {/* Search Individual Mom */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-[#FAE0D5] p-5 mb-4"
        >
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-[#D98DAE]" />
            <h3 className="font-semibold text-[#30244F]">Exportar mãe específica</h3>
          </div>
          
          {selectedReport ? (
            // Mãe selecionada
            <div className="flex items-center justify-between bg-[#D98DAE]/10 rounded-xl p-4 border-2 border-[#D98DAE]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D98DAE] flex items-center justify-center text-white font-bold">
                  {selectedReport.baby_name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="font-semibold text-[#30244F]">{selectedReport.baby_name}</p>
                  <p className="text-sm text-[#6B5B8C]">
                    {selectedReport.mother_name || 'Mãe'} • {selectedReport.baby_age ? `${selectedReport.baby_age} meses` : ''}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="text-[#D98DAE] hover:bg-[#D98DAE]/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          ) : (
            // Campo de busca
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9890A8]" />
                <input
                  type="text"
                  placeholder="Buscar por nome do bebê, mãe ou email..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#FAE0D5] focus:border-[#D98DAE] focus:ring-2 focus:ring-[#D98DAE]/20 outline-none transition-all"
                />
              </div>
              
              {/* Dropdown de resultados */}
              {showDropdown && searchTerm && (
                <div className="absolute z-10 w-full mt-2 bg-white rounded-xl border border-[#FAE0D5] shadow-lg max-h-60 overflow-y-auto">
                  {loadingReports ? (
                    <div className="p-4 text-center text-[#9890A8]">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </div>
                  ) : filteredReports.length === 0 ? (
                    <div className="p-4 text-center text-[#9890A8]">
                      Nenhum resultado encontrado
                    </div>
                  ) : (
                    filteredReports.slice(0, 10).map((report) => (
                      <button
                        key={report.id}
                        onClick={() => handleSelectReport(report)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#FBF4FF] transition-colors text-left border-b border-[#FAE0D5] last:border-0"
                      >
                        <div className="w-8 h-8 rounded-full bg-[#8F7AC5]/20 flex items-center justify-center text-[#8F7AC5] font-semibold text-sm">
                          {report.baby_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#30244F] truncate">{report.baby_name || 'Sem nome'}</p>
                          <p className="text-xs text-[#9890A8] truncate">
                            {report.mother_name || 'Mãe'} • {report.email || ''}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          report.status === 'approved' ? 'bg-green-100 text-green-700' :
                          report.status === 'pending_analysis' ? 'bg-blue-100 text-blue-700' :
                          report.status === 'pending_routine' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {report.status === 'approved' ? 'Aprovado' :
                           report.status === 'pending_analysis' ? 'Análise' :
                           report.status === 'pending_routine' ? 'Rotina' : 'Pendente'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Filter Section */}
        {!selectedReport && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-[#FAE0D5] p-5 mb-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-[#8F7AC5]" />
              <h3 className="font-semibold text-[#30244F]">Ou filtrar por status</h3>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedFilter(option.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-2 ${
                    selectedFilter === option.value
                      ? `${option.color} ring-2 ring-offset-2 ring-[#8F7AC5]`
                      : 'bg-white text-[#6B5B8C] border-[#FAE0D5] hover:border-[#8F7AC5]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Selected Filter Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-[#8F7AC5]/10 rounded-xl p-4 mb-6 flex items-center gap-3"
        >
          <CheckCircle className="w-5 h-5 text-[#8F7AC5]" />
          <p className="text-sm text-[#30244F]">
            <strong>Exportando:</strong> {getFilterLabel()}
          </p>
        </motion.div>

        {/* Export Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          
          {/* JSON Export */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl border border-[#FAE0D5] p-6 hover:shadow-lg transition-shadow"
          >
            <div className="w-14 h-14 rounded-xl bg-[#8F7AC5]/10 flex items-center justify-center mb-4">
              <FileJson className="w-7 h-7 text-[#8F7AC5]" />
            </div>
            <h3 className="text-lg font-semibold text-[#30244F] mb-2">Exportar JSON</h3>
            <p className="text-sm text-[#6B5B8C] mb-4">
              Exporta dados completos em formato JSON. Ideal para backup ou migração para outro sistema.
            </p>
            <ul className="text-xs text-[#9890A8] mb-4 space-y-1">
              <li>- Dados básicos das clientes</li>
              <li>- Questionários completos</li>
              <li>- Rotinas de 5 dias</li>
              <li>- Planos de sono gerados</li>
            </ul>
            <Button
              onClick={handleExportJSON}
              disabled={exporting !== null}
              className="w-full bg-[#8F7AC5] hover:bg-[#7A68A8] text-white"
            >
              {exporting === 'json' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar JSON
                </>
              )}
            </Button>
          </motion.div>

          {/* CSV Clients Export */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-[#FAE0D5] p-6 hover:shadow-lg transition-shadow"
          >
            <div className="w-14 h-14 rounded-xl bg-[#D98DAE]/10 flex items-center justify-center mb-4">
              <FileSpreadsheet className="w-7 h-7 text-[#D98DAE]" />
            </div>
            <h3 className="text-lg font-semibold text-[#30244F] mb-2">CSV de Clientes</h3>
            <p className="text-sm text-[#6B5B8C] mb-4">
              Exporta dados resumidos das clientes. Ideal para planilhas e análises.
            </p>
            <ul className="text-xs text-[#9890A8] mb-4 space-y-1">
              <li>- Nome do bebê e idade</li>
              <li>- Dados de contato</li>
              <li>- Status do atendimento</li>
              <li>- Principais dificuldades</li>
            </ul>
            <Button
              onClick={handleExportCSV}
              disabled={exporting !== null}
              className="w-full bg-[#D98DAE] hover:bg-[#C77A9D] text-white"
            >
              {exporting === 'csv' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar CSV
                </>
              )}
            </Button>
          </motion.div>

          {/* CSV Routines Export */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl border border-[#FAE0D5] p-6 hover:shadow-lg transition-shadow"
          >
            <div className="w-14 h-14 rounded-xl bg-[#7BC47F]/10 flex items-center justify-center mb-4">
              <FileSpreadsheet className="w-7 h-7 text-[#7BC47F]" />
            </div>
            <h3 className="text-lg font-semibold text-[#30244F] mb-2">CSV de Rotinas</h3>
            <p className="text-sm text-[#6B5B8C] mb-4">
              Exporta todas as rotinas detalhadas. Uma linha por dia registrado.
            </p>
            <ul className="text-xs text-[#9890A8] mb-4 space-y-1">
              <li>- Horários de acordar/dormir</li>
              <li>- Despertares noturnos</li>
              <li>- Sonecas detalhadas</li>
              <li>- Atividades do dia</li>
            </ul>
            <Button
              onClick={handleExportRoutines}
              disabled={exporting !== null}
              className="w-full bg-[#7BC47F] hover:bg-[#5BA35F] text-white"
            >
              {exporting === 'routines' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Rotinas
                </>
              )}
            </Button>
          </motion.div>
        </div>

        {/* Last Export Info */}
        {lastExport && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Última exportação: {lastExport.type}
                {lastExport.records !== undefined && ` (${lastExport.records} registro(s))`}
              </p>
              <p className="text-xs text-green-600">
                Filtro: {lastExport.filter} - {lastExport.time.toLocaleString('pt-BR')}
              </p>
            </div>
          </motion.div>
        )}

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 bg-[#FBF4FF] rounded-xl p-5 border border-[#FAE0D5]"
        >
          <h4 className="font-semibold text-[#30244F] mb-2">Sobre os formatos de exportação</h4>
          <div className="text-sm text-[#6B5B8C] space-y-2">
            <p>
              <strong>JSON:</strong> Formato completo com todos os dados estruturados. 
              Útil para backup completo, migração de dados ou integração com outros sistemas.
            </p>
            <p>
              <strong>CSV de Clientes:</strong> Formato tabular com dados resumidos de cada cliente. 
              Pode ser aberto no Excel, Google Sheets ou qualquer programa de planilhas.
            </p>
            <p>
              <strong>CSV de Rotinas:</strong> Formato tabular detalhado com cada dia de rotina registrado. 
              Ideal para análises de padrões de sono e estatísticas.
            </p>
          </div>
        </motion.div>
      </main>
      
      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default AdminExportPage;
